/// <reference types="vite/client" />
import { MOCK_POSTS, MOCK_CATEGORIES, BloggerPost, BloggerResponse } from './mockData';
import { FETCHED_PAGES } from './fetchedPages';
import { getAccessToken, clearCachedAccessToken } from '../lib/firebase';

const API_KEY = import.meta.env.VITE_BLOGGER_API_KEY?.trim() || '';
const BLOG_ID = import.meta.env.VITE_BLOG_ID?.trim() || '';
const BASE_URL = `https://www.googleapis.com/blogger/v3/blogs/${BLOG_ID}`;

const HAS_CREDENTIALS = API_KEY && BLOG_ID && API_KEY !== '' && BLOG_ID !== '';

export interface BloggerComment {
  id: string;
  published: string;
  updated: string;
  content: string;
  author: {
    id: string;
    displayName: string;
    url: string;
    image: { url: string };
  };
}

export const bloggerService = {
  async authenticatedRequest(endpoint: string, method = 'GET', body?: any): Promise<any> {
    const token = getAccessToken();
    if (!token) {
      throw new Error("User must be authenticated to perform this action.");
    }

    const headers: Record<string, string> = {
       'Authorization': `Bearer ${token}`,
       'Content-Type': 'application/json'
    };

    const res = await fetch(`${BASE_URL}${endpoint}`, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined
    });

    if (!res.ok) {
       let errorMsg = res.statusText;
       try {
         const errorBody = await res.json();
         errorMsg = errorBody.error?.message || res.statusText;
       } catch (e) {}

       if (res.status === 401) {
          clearCachedAccessToken();
          throw new Error("UNAUTHORIZED: Your login session has expired. Please sign in again.");
       }

       if (res.status === 403 && (errorMsg.toLowerCase().includes('disabled') || errorMsg.toLowerCase().includes('not been used'))) {
          throw new Error(`Blogger API has not been enabled in your Google Project. Please follow these steps to enable it: \n\n1. Visit: https://console.developers.google.com/apis/api/blogger.googleapis.com/overview?project=404929563583 \n2. Click "Enable" associated with the Blogger API. \n3. Wait 2-3 minutes, then try again.\n\n(আপনার Google Project-এ Blogger API এনাবল করা নেই। দয়া করে উপরের লিঙ্কে প্রবেশ করে API-টি "Enable" করুন এবং ২-৩ মিনিট পর আবার চেষ্টা করুন।)`);
       }
       throw new Error(`Blogger API error: ${res.status} ${errorMsg}`);
    }

    return res.status === 204 ? null : await res.json();
  },

  async deletePost(postId: string): Promise<void> {
     await this.authenticatedRequest(`/posts/${postId}`, 'DELETE');
  },

  async savePost(postData: any, isDraft = false, postId?: string, customPermalinkSlug?: string): Promise<BloggerPost> {
    const originalTitle = postData.title;
    const isCustomSlugRequested = !!(customPermalinkSlug && customPermalinkSlug.trim());

    const payload: any = {
      ...postData,
      customMetaData: (postData.customMetaData || '').trim() ? postData.customMetaData.trim() : "",
      kind: 'blogger#post',
      blog: { id: BLOG_ID }
    };

    // If inserted directly as published AND custom slug requested:
    if (!postId && !isDraft && isCustomSlugRequested) {
       payload.title = customPermalinkSlug?.trim();
    }

    if (postId) {
       payload.id = postId;

       // If updating from Draft to Published, we need this trick.
       // First, let's fetch the actual current post state to see if it's currently a draft.
       let currentPost: any = null;
       try {
           currentPost = await this.authenticatedRequest(`/posts/${postId}?view=ADMIN`);
       } catch(e) {}
       
       const currentlyDraft = currentPost && currentPost.status === 'DRAFT';
       const willPublishNow = !isDraft && currentlyDraft;

       if (willPublishNow && isCustomSlugRequested) {
           payload.title = customPermalinkSlug?.trim();
       }

       let finalPost = await this.authenticatedRequest(`/posts/${postId}?view=ADMIN`, 'PATCH', payload);
       const currentIsDraft = finalPost.status && finalPost.status.toUpperCase() === 'DRAFT';
       
       if (isDraft && !currentIsDraft) {
           finalPost = await this.authenticatedRequest(`/posts/${postId}/revert`, 'POST');
       } else if (!isDraft && currentIsDraft) {
           finalPost = await this.authenticatedRequest(`/posts/${postId}/publish`, 'POST');
       }

       // After publishing from draft, restore the original title if we did the slug trick
       if (willPublishNow && isCustomSlugRequested) {
            return await this.authenticatedRequest(`/posts/${postId}?view=ADMIN`, 'PATCH', {
                id: postId,
                title: originalTitle,
                kind: 'blogger#post',
                blog: { id: BLOG_ID }
            });
       }

       return finalPost;
    } else {
       // insert
       const insertedPost = await this.authenticatedRequest(`/posts/?isDraft=${isDraft}&view=ADMIN`, 'POST', payload);
       
       if (!isDraft && isCustomSlugRequested) {
           // Restore original title immediately after publish
           return await this.authenticatedRequest(`/posts/${insertedPost.id}?view=ADMIN`, 'PATCH', {
               id: insertedPost.id,
               title: originalTitle,
               kind: 'blogger#post',
               blog: { id: BLOG_ID }
           });
       }
       return insertedPost;
    }
  },

  async getAdminPosts(pageToken?: string, status?: string[]): Promise<{ items: BloggerPost[], nextPageToken?: string }> {
     // For admin, we use authenticated requests if they are logged in via Google OAuth
     const token = getAccessToken();
     let url = new URL(`${BASE_URL}/posts`);
     let useAuth = false;

     if (token) {
        useAuth = true;
     } else {
        url.searchParams.append('key', API_KEY);
     }
     
     url.searchParams.append('maxResults', '50');
     url.searchParams.append('view', 'ADMIN');
     if (pageToken && pageToken !== '') url.searchParams.append('pageToken', pageToken);
     if (useAuth) {
       if (status && status.length > 0) {
         status.forEach(val => url.searchParams.append('status', val.toLowerCase())); // drafts, live, scheduled
       } else {
         url.searchParams.append('status', 'live');
         url.searchParams.append('status', 'draft');
         url.searchParams.append('status', 'scheduled');
       }
     } else {
       // When using API key without OAuth, we cannot fetch drafts.
       // Default is to only get live posts.
       url.searchParams.append('status', 'live');
     }

     const reqInit: RequestInit = useAuth ? {
        headers: { 'Authorization': `Bearer ${token}` }
     } : {};

     try {
       const res = await fetch(url.toString(), reqInit);
       if (!res.ok) {
          if (res.status === 403 && useAuth) {
             console.warn("User does not have admin access to this blog, falling back to public posts.");
             return this.getPosts(pageToken, 50);
          }
          throw new Error(`Failed to fetch admin posts: ${res.status}`);
       }
       const data = await res.json();
       return { items: data.items || [], nextPageToken: data.nextPageToken };
     } catch (err: any) {
       console.warn("Network error in getAdminPosts:", err.message);
       // Throw error so caller UI can show proper failure state if needed, or fallback
       throw err;
     }
  },

  async changePostStatus(postId: string, isDraft: boolean): Promise<any> {
    const action = isDraft ? 'revert' : 'publish';
    return await this.authenticatedRequest(`/posts/${postId}/${action}`, 'POST');
  },

  async getComments(postId: string): Promise<{ items: BloggerComment[] }> {
    if (!HAS_CREDENTIALS) {
      return { items: [] };
    }

    try {
      const url = new URL(`${BASE_URL}/posts/${postId}/comments`);
      url.searchParams.append('key', API_KEY);
      
      const res = await fetch(url.toString());
      if (!res.ok) {
        console.warn(`Failed to fetch comments: ${res.statusText}`);
        return { items: [] };
      }
      const data = await res.json();
      return { items: data.items || [] };
    } catch (e) {
      console.warn('Network error in getComments, falling back to empty');
      return { items: [] };
    }
  },

  async getPosts(pageToken?: string, maxResults = 10, startDate?: string, endDate?: string): Promise<{ items: BloggerPost[], nextPageToken?: string }> {
    if (!HAS_CREDENTIALS) {
      console.warn("Blogger credentials not found. Using mock data.");
      return new Promise((resolve) => setTimeout(() => resolve({ items: MOCK_POSTS }), 800));
    }

    try {
      const url = new URL(`${BASE_URL}/posts`);
      url.searchParams.append('key', API_KEY);
      url.searchParams.append('maxResults', maxResults.toString());
      url.searchParams.append('fetchImages', 'true');
      if (pageToken && pageToken !== '') url.searchParams.append('pageToken', pageToken);
      if (startDate) url.searchParams.append('startDate', startDate);
      if (endDate) url.searchParams.append('endDate', endDate);

      const res = await fetch(url.toString());
      if (!res.ok) {
        const errorText = await res.text();
        console.warn(`Failed to fetch posts: ${res.status} ${res.statusText} - ${errorText}`);
        return { items: MOCK_POSTS, nextPageToken: undefined };
      }
      const data: BloggerResponse = await res.json();
      return { items: data.items || [], nextPageToken: data.nextPageToken };
    } catch (e) {
      console.warn('Network error in getPosts, falling back to mock data');
      return { items: MOCK_POSTS };
    }
  },

  async getPostById(postId: string): Promise<BloggerPost> {
    if (!HAS_CREDENTIALS) {
      const post = MOCK_POSTS.find(p => p.id === postId) || MOCK_POSTS[0];
      return new Promise((resolve) => setTimeout(() => resolve(post), 400));
    }

    const token = getAccessToken();
    if (token) {
      try {
        // Use auth to get things like customMetaData
        return await this.authenticatedRequest(`/posts/${postId}?view=ADMIN`);
      } catch (authError: any) {
        console.warn('Authenticated getPostById failed, falling back to public fetch:', authError.message || authError);
        if (authError.message?.includes('UNAUTHORIZED') || authError.message?.includes('401')) {
           clearCachedAccessToken();
        }
      }
    }

    try {
      const url = new URL(`${BASE_URL}/posts/${postId}`);
      url.searchParams.append('key', API_KEY);
      
      const res = await fetch(url.toString());
      if (!res.ok) {
        console.warn(`Failed to fetch post by ID publicly: ${res.status} ${res.statusText}`);
        return MOCK_POSTS.find(p => p.id === postId) || MOCK_POSTS[0];
      }
      return await res.json();
    } catch (e: any) {
      console.warn('Network error in public getPostById, falling back to mock data:', e.message || e);
      return MOCK_POSTS.find(p => p.id === postId) || MOCK_POSTS[0];
    }
  },

  async getPostByPath(path: string): Promise<BloggerPost | null> {
    if (!HAS_CREDENTIALS) {
      return new Promise((resolve) => setTimeout(() => resolve(MOCK_POSTS[0] || null), 400));
    }

    try {
      const url = new URL(`${BASE_URL}/posts/bypath`);
      url.searchParams.append('key', API_KEY);
      url.searchParams.append('path', path);
      
      const res = await fetch(url.toString());
      if (!res.ok) {
        if (res.status === 404) return null;
        console.warn(`Failed to fetch post by path: ${res.statusText}`);
        return null;
      }
      return await res.json();
    } catch (e) {
      console.warn('Network error in getPostByPath');
      return null;
    }
  },

  async getPostsByLabel(label: string, maxResults = 10, pageToken?: string, startDate?: string): Promise<{ items: BloggerPost[], nextPageToken?: string }> {
    if (!label) {
      return this.getPosts(pageToken, maxResults, startDate);
    }
    
    if (!HAS_CREDENTIALS) {
      return new Promise((resolve) => setTimeout(() => resolve({ 
        items: MOCK_POSTS.filter(p => p.labels?.includes(label)) 
      }), 600));
    }

    try {
      const url = new URL(`${BASE_URL}/posts`);
      url.searchParams.append('key', API_KEY);
      url.searchParams.append('labels', label);
      url.searchParams.append('maxResults', maxResults.toString());
      url.searchParams.append('fetchImages', 'true');
      if (pageToken && pageToken !== '') url.searchParams.append('pageToken', pageToken);
      if (startDate) url.searchParams.append('startDate', startDate);

      const res = await fetch(url.toString());
      if (!res.ok) {
        const errorText = await res.text();
        console.warn(`Failed to fetch posts by label: ${res.status} ${res.statusText} - ${errorText}`);
        return { items: MOCK_POSTS.filter(p => p.labels?.includes(label)) };
      }
      const data: BloggerResponse = await res.json();
      return { items: data.items || [], nextPageToken: data.nextPageToken };
    } catch (e) {
      console.warn('Network error in getPostsByLabel, falling back to mock data');
      return { items: MOCK_POSTS.filter(p => p.labels?.includes(label)) };
    }
  },

  async searchPosts(query: string): Promise<{ items: BloggerPost[] }> {
    if (!HAS_CREDENTIALS) {
      return new Promise((resolve) => setTimeout(() => resolve({ 
        items: MOCK_POSTS.filter(p => p.title.toLowerCase().includes(query.toLowerCase())) 
      }), 600));
    }

    try {
      const url = new URL(`${BASE_URL}/posts/search`);
      url.searchParams.append('key', API_KEY);
      url.searchParams.append('q', query);
      url.searchParams.append('fetchImages', 'true');

      const res = await fetch(url.toString());
      if (!res.ok) {
        console.warn(`Failed to search posts: ${res.statusText}`);
        return { items: MOCK_POSTS.filter(p => p.title.toLowerCase().includes(query.toLowerCase())) };
      }
      const data: BloggerResponse = await res.json();
      return { items: data.items || [] };
    } catch (e: any) {
      console.warn('Fetch warning in searchPosts:', e.message || e);
      return { items: MOCK_POSTS.filter(p => p.title.toLowerCase().includes(query.toLowerCase())) };
    }
  },

  async getPages(): Promise<{ items: BloggerPost[] }> {
    if (!HAS_CREDENTIALS) {
      return { items: FETCHED_PAGES };
    }

    try {
      const url = new URL(`${BASE_URL}/pages`);
      url.searchParams.append('key', API_KEY);
      
      const res = await fetch(url.toString());
      if (!res.ok) {
        console.warn(`Failed to fetch pages: ${res.statusText}`);
        return { items: FETCHED_PAGES };
      }
      const data = await res.json();
      const apiItems = data.items || [];
      // Combine API pages with our fetched static pages so both are available
      return { items: [...apiItems, ...FETCHED_PAGES] };
    } catch (e: any) {
      console.warn('Fetch warning in getPages:', e.message || e);
      return { items: FETCHED_PAGES };
    }
  },

  async getCategories(): Promise<string[]> {
    // Note: Blogger API doesn't have a direct endpoint purely for retrieving ALL labels easily without looping posts.
    // For a real prod app, you either hardcode main categories or extract from recent posts.
    if (!HAS_CREDENTIALS) {
      return MOCK_CATEGORIES;
    }
    
    try {
      const url = `${BASE_URL}/posts?key=${API_KEY}&maxResults=100&fetchImages=true`;
      const res = await fetch(url);
      if (res.ok) {
        const data = await res.json();
        const items = data.items || [];
        const labelsSet = new Set<string>();
        items.forEach((post: any) => {
          post.labels?.forEach((l: string) => {
            if (l && l.trim() !== '') {
              labelsSet.add(l.trim());
            }
          });
        });
        
        const realLabels = Array.from(labelsSet);
        if (realLabels.length > 0) {
          return realLabels;
        }
      }
    } catch (e: any) {
      console.warn('Failed to extract categories dynamically:', e.message || e);
    }
    
    return [
      "জাতীয়", "বাংলাদেশ", "বিশ্ব", "রাজনীতি", "অর্থনীতি", 
      "আদালত", "শিক্ষা", "ধর্ম", "প্রযুক্তি", "ব্যাক্তিত্ব", 
      "ফিচার", "খেলাধুলা", "বিনোদন", "ভ্রমণ", "লাইফস্টাইল", 
      "রান্নাঘর", "সম্পাদকীয়", "মুক্তিযুদ্ধ", "ক্রাইম", "পরিবেশ", 
      "প্রবাস", "বাণিজ্য", "কৃষিবার্তা", "ফেসবুক", "শিক্ষাঙ্গন", 
      "চাকরির খবর", "সাহিত্য", "পর্যটন", "রূপচর্চা", "স্বাস্থ্য",
      "Seba Top News", "Trending", "Special", "Exclusive"
    ];
  }
};
